import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { Order, OrderTimelineEvent } from '../types';

export const createOrder = async (order: Omit<Order, 'id' | 'createdAt'>): Promise<string> => {
  const startTime = Date.now();
  console.log(`[Order] Creating new order:`, {
    timestamp: new Date().toISOString(),
    customer: order.customer.email,
    total: order.total,
    itemsCount: order.items.length,
    language: order.language || 'en',
  });

  try {
    const ordersRef = collection(db, 'orders');
    
    // Create initial timeline event
    const createdEvent: OrderTimelineEvent = {
      id: `event_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      type: 'created',
      description: 'Order created',
      createdAt: new Date(),
    };

    const docRef = await addDoc(ordersRef, {
      ...order,
      timeline: [createdEvent],
      createdAt: new Date()
    });

    console.log(`[Order] Order created successfully:`, {
      timestamp: new Date().toISOString(),
      orderId: docRef.id,
      duration: `${Date.now() - startTime}ms`,
    });

    // Send email notification for order created (fire and forget)
    // Call internal API route to send email
    if (typeof window === 'undefined') {
      // Server-side: call directly
      console.log(`[Order] Sending order creation email (server-side):`, {
        timestamp: new Date().toISOString(),
        orderId: docRef.id,
        email: order.customer.email,
        language: order.language || 'en',
      });
      // Server-side email sending should be handled by backend API
      // This code path should not be reached in a Vite frontend
      console.warn(`[Order] Server-side email sending not available in frontend:`, {
        timestamp: new Date().toISOString(),
        orderId: docRef.id,
      });
    } else {
      // Client-side: call backend API route
      console.log(`[Order] Triggering order creation email (client-side):`, {
        timestamp: new Date().toISOString(),
        orderId: docRef.id,
        email: order.customer.email,
      });
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      fetch(`${apiBaseUrl}/api/orders/${docRef.id}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId: docRef.id,
          type: 'order_created',
        }),
      })
      .then(() => {
        console.log(`[Order] Order creation email API called successfully:`, {
          timestamp: new Date().toISOString(),
          orderId: docRef.id,
        });
      })
      .catch((err) => {
        console.error(`[Order] Error calling email API:`, {
          timestamp: new Date().toISOString(),
          orderId: docRef.id,
          error: err.message || String(err),
        });
      });
    }

    return docRef.id;
  } catch (error: any) {
    console.error(`[Order] Error creating order:`, {
      timestamp: new Date().toISOString(),
      error: error.message || String(error),
      duration: `${Date.now() - startTime}ms`,
    });
    throw error;
  }
};

export const getOrder = async (orderId: string): Promise<Order | null> => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (orderSnap.exists()) {
      return {
        id: orderSnap.id,
        ...orderSnap.data()
      } as Order;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting order:', error);
    throw error;
  }
};

